import { useMemo, useState } from 'react';
import {
  Accordion,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@design-system';
import { Plus, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { CARRIER_CATALOG } from '@shared/underwriting';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  ImportRulesDialog,
  LoadingBlock,
  RefreshButton,
  RuleDetailSheet,
  RunResultsTable,
  TestCaseAccordionItem,
  TestCaseForm,
  useWizardEvalRules,
  useWizardEvalTestCases,
  type WizardEvalRule,
} from '@/features/wizard-evals';

const DEFAULT_TAB = 'test-cases';

export function WizardEvalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') ?? DEFAULT_TAB;
  const [createOpen, setCreateOpen] = useState(false);
  const [importRulesOpen, setImportRulesOpen] = useState(false);
  const [rulesCarrier, setRulesCarrier] = useState<string>('all');
  const [rulesSearch, setRulesSearch] = useState('');
  const [ruleSheetOpen, setRuleSheetOpen] = useState(false);
  const [activeRule, setActiveRule] = useState<WizardEvalRule | null>(null);

  const {
    data: testCases,
    isLoading: testCasesInitialLoading,
    isFetching: testCasesFetching,
    refetch: refetchTestCases,
  } = useWizardEvalTestCases();
  const {
    data: rules,
    isLoading: rulesInitialLoading,
    isFetching: rulesFetching,
    refetch: refetchRules,
  } = useWizardEvalRules(
    rulesCarrier === 'all' ? undefined : { carrier: rulesCarrier },
  );

  const filteredRules = useMemo(() => {
    if (!rules) return [];
    const q = rulesSearch.trim().toLowerCase();
    if (!q) return rules;
    const tokens = q.split(/\s+/).filter(Boolean);
    return rules.filter(r => {
      const haystack = `${r.ruleId} ${r.ruleText} ${r.category ?? ''} ${r.carrier}`.toLowerCase();
      return tokens.every(t => haystack.includes(t));
    });
  }, [rules, rulesSearch]);

  const openNewRule = () => {
    setActiveRule(null);
    setRuleSheetOpen(true);
  };

  const openExistingRule = (rule: WizardEvalRule) => {
    setActiveRule(rule);
    setRuleSheetOpen(true);
  };

  const onTabChange = (tab: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Underwriting Evals"
        description="Author test cases, manage the rule catalog, and run the Assessor → Reviewer pipeline against stored snapshots."
      />

      <Tabs
        value={selectedTab}
        onValueChange={onTabChange}
      >
        <TabsList className="bg-primary-100 mb-6 h-12 p-1">
          <TabsTrigger
            value="test-cases"
            className="h-full px-6 text-base font-semibold"
          >
            Test Cases
          </TabsTrigger>
          <TabsTrigger
            value="rules"
            className="h-full px-6 text-base font-semibold"
          >
            Rules
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            className="h-full px-6 text-base font-semibold"
          >
            Metrics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="test-cases">
          <p className="mb-4 text-sm text-gray-600">
            Snapshot a real client intake and set the expected outcome, rate class, and rule IDs.
            Each test case can be run individually to see how the model performs on that snapshot.
          </p>
          <div className="mb-4 flex items-center justify-between gap-2">
            <p className="text-sm text-gray-700">
              {testCasesInitialLoading
                ? ' '
                : `${testCases?.length ?? 0} test case${testCases?.length === 1 ? '' : 's'}`}
            </p>
            <div className="flex items-center gap-2">
              <RefreshButton
                onClick={() => refetchTestCases()}
                isFetching={testCasesFetching}
              />
              <Button
                onClick={() => setCreateOpen(o => !o)}
                variant={createOpen ? 'outline' : 'default'}
              >
                {createOpen ? (
                  <>
                    <X className="mr-1 h-4 w-4" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Plus className="mr-1 h-4 w-4" />
                    New test case
                  </>
                )}
              </Button>
            </div>
          </div>

          {createOpen && (
            <div className="mb-4 rounded border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">New test case</h3>
              </div>
              <TestCaseForm
                mode="create"
                onSuccess={() => setCreateOpen(false)}
                onCancel={() => setCreateOpen(false)}
              />
            </div>
          )}

          {testCasesInitialLoading && <LoadingBlock label="Loading test cases…" />}

          {!testCasesInitialLoading && (testCases?.length ?? 0) === 0 && !createOpen && (
            <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
              No test cases yet. Click <strong>New test case</strong> to snapshot an intake payload
              and set expected outcomes.
            </p>
          )}

          {testCases && testCases.length > 0 && (
            <Accordion
              type="single"
              collapsible
            >
              {testCases.map(tc => (
                <TestCaseAccordionItem
                  key={tc.testCaseId}
                  testCase={tc}
                />
              ))}
            </Accordion>
          )}
        </TabsContent>

        <TabsContent value="rules">
          <p className="mb-4 text-sm text-gray-600">
            The catalog of carrier underwriting rules the pipeline can flag. Test cases reference
            these rule IDs as expected/unexpected outcomes. Import from CSV to keep in sync with the
            carrier source of truth.
          </p>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-1 items-center gap-3">
              <Select
                value={rulesCarrier}
                onValueChange={setRulesCarrier}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All carriers</SelectItem>
                  {CARRIER_CATALOG.map(c => (
                    <SelectItem
                      key={c.name}
                      value={c.name}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={rulesSearch}
                onChange={e => setRulesSearch(e.target.value)}
                placeholder="Search ruleId, text, or category…"
                className="max-w-xs"
              />
              <p className="whitespace-nowrap text-sm text-gray-700">
                {rulesInitialLoading
                  ? ' '
                  : `${filteredRules.length} of ${rules?.length ?? 0}${
                      rules ? ` (${rules.filter(r => r.isActive).length} active)` : ''
                    }`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <RefreshButton
                onClick={() => refetchRules()}
                isFetching={rulesFetching}
              />
              <Button onClick={openNewRule}>
                <Plus className="mr-1 h-4 w-4" />
                New rule
              </Button>
              <Button
                variant="outline"
                onClick={() => setImportRulesOpen(true)}
              >
                <Plus className="mr-1 h-4 w-4" />
                Import CSV
              </Button>
            </div>
          </div>

          {rulesInitialLoading && <LoadingBlock label="Loading rules…" />}

          {!rulesInitialLoading && (rules?.length ?? 0) === 0 && (
            <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
              No rules in the catalog yet. Click <strong>Import CSV</strong> to populate from an
              exported rule catalog.
            </p>
          )}

          {rules && rules.length > 0 && filteredRules.length === 0 && (
            <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
              No rules match &ldquo;{rulesSearch}&rdquo;.
            </p>
          )}

          {filteredRules.length > 0 && (
            <ul className="divide-y divide-gray-200 rounded border border-gray-200">
              {filteredRules.map(r => (
                <li
                  key={r.ruleId}
                  className="p-0"
                >
                  <button
                    type="button"
                    onClick={() => openExistingRule(r)}
                    className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-xs text-gray-900">{r.ruleId}</p>
                      <p className="truncate text-xs text-gray-600">{r.ruleText}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-xs">
                      {r.category && (
                        <span className="bg-primary-50 text-primary-dark rounded px-1.5 py-0.5">
                          {r.category}
                        </span>
                      )}
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                        {r.carrier}
                      </span>
                      {!r.isActive && (
                        <span className="rounded bg-orange-100 px-1.5 py-0.5 text-orange-700">
                          inactive
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <ImportRulesDialog
            open={importRulesOpen}
            onOpenChange={setImportRulesOpen}
          />

          <RuleDetailSheet
            open={ruleSheetOpen}
            onOpenChange={setRuleSheetOpen}
            rule={activeRule}
          />
        </TabsContent>

        <TabsContent value="runs">
          <p className="mb-4 text-sm text-gray-600">
            Every run across every test case — one row per result. Filter by carrier, model, or
            prompt version, and select rows to aggregate precision, recall, and F1 across a subset.
            Use this to compare how different model or prompt versions perform on the same test
            cases.
          </p>
          <RunResultsTable />
        </TabsContent>
      </Tabs>
    </main>
  );
}
